import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useRangeState } from '../../hooks/useRangeState';
import type { RangeConfigurableFilter } from '../../types';

export type RangeSelectorProps = {
  filterKey: string;
  selectedOptions?: [string | undefined, string | undefined];
  onOptionChange: (value?: [string | undefined, string | undefined]) => void;
  isDisabled?: boolean;
  setValidationState?: (value: boolean) => void;
} & Pick<RangeConfigurableFilter<unknown>, 'config'>;

const inputStyle = {
  marginLeft: 0.5,
  marginRight: 0.5,
};

export const RangeSelector = ({
  filterKey,
  selectedOptions,
  onOptionChange,
  setValidationState,
  isDisabled,
  config,
}: RangeSelectorProps) => {
  const { t } = useTranslation();

  const { min, max, errors, handleChange } = useRangeState(
    filterKey,
    selectedOptions,
    onOptionChange,
    config,
    setValidationState
  );

  const inputMode = config?.inputType === 'float' ? 'decimal' : 'numeric';
  const minPlaceholder =
    config?.detailedOptionsConfig?.minFieldPlaceholder ?? config?.fieldPlaceholder ?? t('Enter value');
  const maxPlaceholder =
    config?.detailedOptionsConfig?.maxFieldPlaceholder ?? config?.fieldPlaceholder ?? t('Enter value');
  const adornmentProps = {
    startAdornment: config?.startAdornment,
    endAdornment: config?.endAdornment,
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      data-test="range-filter-selector"
      sx={{
        alignItems: 'flex-start',
      }}
    >
      <Stack
        spacing={0.5}
        sx={{
          flex: 1,
        }}
      >
        <FormLabel>{t('Min')}</FormLabel>
        <TextField
          data-test="range-filter-min-input"
          variant="outlined"
          size="small"
          value={min}
          onChange={handleChange('min')}
          disabled={isDisabled}
          error={!!errors.min}
          helperText={errors.min}
          placeholder={minPlaceholder}
          slotProps={{
            input: adornmentProps,
            htmlInput: {
              inputMode,
              type: 'text',
              step: config?.precision ? `0.${'0'.repeat(config.precision - 1)}1` : '1',
              'aria-label': 'Min',
              sx: inputStyle,
            },
          }}
        />
      </Stack>
      {/* Separator */}
      <Typography variant="body1" sx={{ height: theme => theme.spacing(4), marginTop: '20px !important' }}>
        &ndash;
      </Typography>
      <Stack
        spacing={0.5}
        sx={{
          flex: 1,
        }}
      >
        <FormLabel>{t('Max')}</FormLabel>
        <TextField
          data-test="range-filter-max-input"
          variant="outlined"
          size="small"
          value={max}
          onChange={handleChange('max')}
          disabled={isDisabled}
          error={!!errors.max}
          helperText={errors.max}
          placeholder={maxPlaceholder}
          slotProps={{
            input: adornmentProps,
            htmlInput: {
              inputMode,
              type: 'text',
              step: config?.precision ? `0.${'0'.repeat(config.precision - 1)}1` : '1',
              'aria-label': 'Max',
              sx: inputStyle,
            },
          }}
        />
      </Stack>
    </Stack>
  );
};
